
import { defineComponent, onBeforeMount, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheTime from '@/common/components/UI/TheTime/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

import { AddUserDiscountPayload, DiscountType } from '@/core/types/delivery-discounts.types'
import { User } from '@/core/types/delivery-users.types'

import { deliveryDiscounts } from '@/core/realisations/delivery-discounts.realisation'
import { deliveryUsers } from '@/core/realisations/delivery-users.realisation'

import useNotifications from '@/common/composable/useNotifications'

import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

export default defineComponent({
    components: {
        TheSelect,
        TheInput,
        TheTime,
        TheButton,
    },
    setup() {
        const notifications = useNotifications()
        const router = useRouter()

        const is_loading = ref(false)

        const state = reactive({
            discountTypeId: null as number | null,
            userId: null as number | null,
            expirationDate: null as string | null,
            expirationTime: null as string | null,
        })

        const state_form$ = useVuelidate(
            {
                discountTypeId: { required },
                userId: { required },
                expirationDate: { required },
                expirationTime: { required },
            },
            state
        )

        const discounts_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as DiscountType[],
        })

        const users_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as User[],
        })

        const formatDateStringToApi = (dateStr: string | null): string => {
            if (!dateStr) return ''

            const date = new Date(dateStr)

            let day = date.getDate() as string | number
            let month = (date.getMonth() + 1) as string | number

            const year = date.getFullYear()

            if ((day as number) < 10) {
                day = `0${day}`
            }

            if ((month as number) < 10) {
                month = `0${month}`
            }

            return `${day}.${month}.${year}`
        }

        const onSubmit = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                is_loading.value = true

                const payload = {
                    discountTypeId: state.discountTypeId as number,
                    userId: state.userId as number,
                    deadline: `${formatDateStringToApi(state.expirationDate)} ${
                        state.expirationTime
                    }:00`,
                } as AddUserDiscountPayload

                await deliveryDiscounts.addUserDiscount(payload)

                notifications.notify({ title: 'Скидка добавлена к компании', type: 'success' })
                router.push('/billing/discounts/spark-kz')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При добавлении скидки к компании произошла ошибка',
                })
            } finally {
                is_loading.value = false
            }
        }

        const onCancel = () => {
            console.log('cancel')
        }

        const loadDiscounts = async () => {
            try {
                discounts_state.is_loading = true

                const response = await deliveryDiscounts.getDiscountTypes({ page: 1, limit: 9999 })
                discounts_state.list = response.data

                discounts_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении скидок произошла ошибка' })
                discounts_state.is_failed = true
            } finally {
                discounts_state.is_loading = false
            }
        }

        const loadUsers = async () => {
            try {
                users_state.is_loading = true

                const response = await deliveryUsers.getUsers({ page: 1, limit: 9999 })
                users_state.list = response.data

                users_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении пользователей произошла ошибка',
                })
                users_state.is_failed = true
            } finally {
                users_state.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadDiscounts()
            loadUsers()
        })

        return {
            is_loading,
            state,
            state_form$,
            discounts_state,
            users_state,
            loadDiscounts,
            loadUsers,
            onSubmit,
            onCancel,
        }
    },
})
